import noiseSrc from "url:./noise.png";
import spriteSrc from "url:./particle.png";
import fragmentShader from "./shader.frag";
import vertexShader from "./shader.vert";

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  Float32BufferAttribute,
  MathUtils,
  MirroredRepeatWrapping,
  Object3D,
  Points,
  PointsMaterial,
  RepeatWrapping,
  ShaderMaterial,
  TextureLoader,
  Vector3,
} from "three";

interface ParticleOptions {
  position?: Vector3;
  spread?: number;
  velocity?: Vector3;
  velocityRandomness?: number;
  lifespan?: number;
  color?: Color;
  speed?: number;
  startTimeOffset?: number;
}

interface SystemConfig {
  maxParticles?: number;
}

const TMP_1 = new Vector3();
const TMP_2 = new Vector3();
const TMP_3 = new Vector3();
const TMP_4 = new Vector3();

class GPUParticles extends Object3D {
  private geometry = new BufferGeometry();
  private material: ShaderMaterial;
  private time = 0;
  private particleCount = 0;
  private particlePositions: BufferAttribute;
  private particlePositionsStartTime: BufferAttribute;
  private particleVelLife: BufferAttribute;
  private particleColSpeed: BufferAttribute;
  private particleSystem: Points;
  private cursor = 0;
  private geometryNeedsUpdate = false;

  constructor({ maxParticles = 100000 }: SystemConfig = {}) {
    super();
    this.particleCount = maxParticles;
    const particlePositionsStartTime = new Float32Array(maxParticles * 4);
    const particleVelLife = new Float32Array(maxParticles * 4);
    const particleColSpeed = new Float32Array(maxParticles * 4);
    const vertices = new Float32Array(maxParticles * 3);

    for (let i = 0; i < maxParticles; i++) {
      vertices[i * 4 + 0] = 0; // pos.x
      vertices[i * 4 + 1] = 0; // pos.y
      vertices[i * 4 + 2] = 0; // pos.z

      particlePositionsStartTime[i * 4 + 0] = MathUtils.randFloatSpread(10); // pos.x
      particlePositionsStartTime[i * 4 + 1] = MathUtils.randFloatSpread(10); // pos.y
      particlePositionsStartTime[i * 4 + 2] = MathUtils.randFloatSpread(10); // pos.z
      particlePositionsStartTime[i * 4 + 3] = -10; // startTime

      const vel = new Vector3(
        MathUtils.randFloatSpread(1),
        MathUtils.randFloatSpread(1),
        MathUtils.randFloatSpread(1)
      ).normalize();
      particleVelLife[i * 4 + 0] = vel.x; // vel.x
      particleVelLife[i * 4 + 1] = vel.y; // vel.y
      particleVelLife[i * 4 + 2] = vel.z; // vel.z
      particleVelLife[i * 4 + 3] = 10; // lifespan

      particleColSpeed[i * 4 + 0] = 0; // col.r
      particleColSpeed[i * 4 + 1] = 0; // col.g
      particleColSpeed[i * 4 + 2] = 0; // col.b
      particleColSpeed[i * 4 + 3] = 0.0001; // speed
    }

    this.particlePositions = new BufferAttribute(vertices, 3);
    this.geometry.setAttribute("position", this.particlePositions);

    this.particlePositionsStartTime = new BufferAttribute(
      particlePositionsStartTime,
      4
    );
    this.particlePositionsStartTime.setUsage(DynamicDrawUsage);
    this.geometry.setAttribute(
      "particlePositionsStartTime",
      this.particlePositionsStartTime
    );

    this.particleVelLife = new BufferAttribute(particleVelLife, 4);
    this.particleVelLife.setUsage(DynamicDrawUsage);
    this.geometry.setAttribute("particleVelLife", this.particleVelLife);

    this.particleColSpeed = new BufferAttribute(particleColSpeed, 4);
    this.particleColSpeed.setUsage(DynamicDrawUsage);
    this.geometry.setAttribute("particleColSpeed", this.particleColSpeed);

    const textureLoader = new TextureLoader();
    const tNoise = textureLoader.load(noiseSrc);
    tNoise.wrapS = tNoise.wrapT = MirroredRepeatWrapping;
    const tSprite = textureLoader.load(spriteSrc);
    tSprite.wrapS = tSprite.wrapT = MirroredRepeatWrapping;

    this.material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      uniforms: {
        uTime: {
          value: 0,
        },
        uScale: {
          value: 50,
        },
        tNoise: {
          value: tNoise,
        },
        tSprite: {
          value: tSprite,
        },
      },
      blending: AdditiveBlending,
      vertexShader,
      fragmentShader,
      side: DoubleSide,
    });
    this.particleSystem = new Points(this.geometry, this.material);
    this.particleSystem.frustumCulled = false;
    this.add(this.particleSystem);
  }

  public spawnParticle({
    position,
    spread = 0,
    velocity,
    velocityRandomness = 0,
    lifespan = 3,
    color = new Color(0xffffff),
    speed = 1,
    startTimeOffset = 0,
  }: ParticleOptions): void {
    const i = this.cursor;

    if (!velocity) {
      TMP_1.x = MathUtils.randFloatSpread(1);
      TMP_1.y = MathUtils.randFloatSpread(1);
      TMP_1.z = MathUtils.randFloatSpread(1);
      TMP_1.normalize();
      velocity = TMP_1;
    }

    if (velocityRandomness > 0) {
      TMP_2.x = MathUtils.randFloatSpread(1);
      TMP_2.y = MathUtils.randFloatSpread(1);
      TMP_2.z = MathUtils.randFloatSpread(1);
      TMP_2.normalize();
      TMP_2.multiplyScalar(velocityRandomness);

      velocity.add(TMP_2).normalize();
    }

    if (!position) {
      TMP_3.x = MathUtils.randFloatSpread(1);
      TMP_3.y = MathUtils.randFloatSpread(1);
      TMP_3.z = MathUtils.randFloatSpread(1);

      position = TMP_3;
    }

    if (spread > 0) {
      TMP_4.x = MathUtils.randFloatSpread(spread);
      TMP_4.y = MathUtils.randFloatSpread(spread);
      TMP_4.z = MathUtils.randFloatSpread(spread);
      position.add(TMP_4);
    }

    this.particlePositionsStartTime.array[i * 4 + 0] = position.x;
    this.particlePositionsStartTime.array[i * 4 + 1] = position.y;
    this.particlePositionsStartTime.array[i * 4 + 2] = position.z;
    this.particlePositionsStartTime.array[i * 4 + 3] =
      this.time - startTimeOffset;

    this.particleVelLife.array[i * 4 + 0] = velocity.x;
    this.particleVelLife.array[i * 4 + 1] = velocity.y;
    this.particleVelLife.array[i * 4 + 2] = velocity.z;
    this.particleVelLife.array[i * 4 + 3] = lifespan;

    this.particleColSpeed.array[i * 4 + 0] = color.r;
    this.particleColSpeed.array[i * 4 + 1] = color.g;
    this.particleColSpeed.array[i * 4 + 2] = color.b;
    this.particleColSpeed.array[i * 4 + 3] = speed * 0.0001;

    this.geometryNeedsUpdate = true;

    this.cursor++;
    if (this.cursor >= this.particleCount) {
      this.cursor = 0;
    }
  }

  public dispose() {}

  public update(delta: number): void {
    this.time += (1 / 60) * delta;
    this.material.uniforms.uTime.value = this.time;

    if (this.geometryNeedsUpdate) {
      this.updateGeometry();
      this.geometryNeedsUpdate = false;
    }
  }

  public updateGeometry() {
    this.particlePositions.needsUpdate = true;
    this.particlePositionsStartTime.needsUpdate = true;
    this.particleVelLife.needsUpdate = true;
    this.particleColSpeed.needsUpdate = true;
  }
}

export default GPUParticles;
